import type { MealItem } from '@cadencia/contracts';
import { requireFood } from '../catalog/foods.js';
import type { Recipe } from '../catalog/recipes.js';
import { roundTo } from '../units/round.js';
import type { MacroTargets } from './targets.js';

/**
 * Composicion nutricional de comidas y recetas.
 *
 * Los macros de una receta NO se almacenan: se calculan desde sus ingredientes
 * cada vez. Un total guardado se queda viejo en cuanto alguien cambia un gramo,
 * y nada avisa de que el numero de la pantalla ya no corresponde con la comida.
 */

export const ZERO_MACROS: MacroTargets = { kcal: 0, proteinG: 0, carbsG: 0, fatG: 0 };

/**
 * Cantidad en la unidad base del alimento (g o ml).
 *
 * `unit` se resuelve con `unitWeightG`. Si el alimento no lo declara, no se
 * inventa un peso: se lanza, porque «una unidad» de algo sin peso conocido no
 * es una cantidad.
 */
export function amountInBaseUnits(item: MealItem): number {
  const food = requireFood(item.foodSlug);
  if (item.unit === 'unit') {
    if (food.unitWeightG === null) {
      throw new Error(`El alimento ${food.slug} no define el peso de una unidad`);
    }
    return item.amount * food.unitWeightG;
  }
  // g y ml se tratan como equivalentes al leer una fila ya guardada: rechazarla
  // aqui dejaria la pantalla en blanco por un desajuste de etiqueta. El test
  // del catalogo si exige que la unidad de cada ingrediente sea la del alimento.
  return item.amount;
}

export function itemMacros(item: MealItem): MacroTargets {
  const food = requireFood(item.foodSlug);
  const factor = amountInBaseUnits(item) / 100;
  return {
    kcal: food.kcal100 * factor,
    proteinG: food.protein100 * factor,
    carbsG: food.carbs100 * factor,
    fatG: food.fat100 * factor,
  };
}

export function sumMacros(macros: readonly MacroTargets[]): MacroTargets {
  return macros.reduce(
    (total, actual) => ({
      kcal: total.kcal + actual.kcal,
      proteinG: total.proteinG + actual.proteinG,
      carbsG: total.carbsG + actual.carbsG,
      fatG: total.fatG + actual.fatG,
    }),
    { ...ZERO_MACROS },
  );
}

export function roundMacros(macros: MacroTargets): MacroTargets {
  return {
    kcal: Math.round(macros.kcal),
    proteinG: roundTo(macros.proteinG, 1),
    carbsG: roundTo(macros.carbsG, 1),
    fatG: roundTo(macros.fatG, 1),
  };
}

export function itemsMacros(items: readonly MealItem[]): MacroTargets {
  return sumMacros(items.map(itemMacros));
}

/** Fibra total de una lista de alimentos, en gramos. */
export function itemsFiberG(items: readonly MealItem[]): number {
  return roundTo(
    items.reduce((total, item) => {
      const food = requireFood(item.foodSlug);
      return total + (food.fiber100 * amountInBaseUnits(item)) / 100;
    }, 0),
    1,
  );
}

/**
 * Ingredientes de una receta como lista de alimentos, opcionalmente escalada.
 * El factor solo se aplica a los ingredientes escalables: multiplicar la sal
 * por 1,4 no acerca a ningun objetivo y estropea el plato.
 */
export function recipeToItems(recipe: Recipe, scale = 1): MealItem[] {
  return recipe.ingredients.map((ingrediente) => {
    const factor = ingrediente.isScalable
      ? Math.min(Math.max(scale, ingrediente.scaleMinPct), ingrediente.scaleMaxPct)
      : 1;
    const cantidad = ingrediente.amount * factor;
    return {
      foodSlug: ingrediente.foodSlug,
      // Las unidades enteras (huevos) no se sirven en fracciones.
      amount:
        ingrediente.unit === 'unit' ? Math.max(1, Math.round(cantidad)) : roundTo(cantidad, 1),
      unit: ingrediente.unit,
    };
  });
}

/**
 * Igual que `recipeToItems`, pero con un factor POR ingrediente.
 *
 * Es la libertad que el modelo de datos ya declaraba y el generador no usaba:
 * `scaleMinPct` y `scaleMaxPct` viven en cada ingrediente, no en la receta. Con
 * un unico factor para todo el plato, subir los hidratos obliga a subir tambien
 * el aceite, y un dia de mucha carga —hidratos altos y grasa en el suelo del
 * 20 %— no cierra por mucho que se escale. Con un factor por ingrediente el
 * arroz sube y el aceite baja dentro de sus propios margenes.
 *
 * Cada factor se acota al margen de SU ingrediente, asi que una lista mal
 * calculada no puede sacar la receta de sus limites.
 */
export function recipeToItemsScaled(recipe: Recipe, factors: readonly number[]): MealItem[] {
  return recipe.ingredients.map((ingrediente, indice) => {
    const pedido = factors[indice] ?? 1;
    const factor = ingrediente.isScalable
      ? Math.min(Math.max(pedido, ingrediente.scaleMinPct), ingrediente.scaleMaxPct)
      : 1;
    const cantidad = ingrediente.amount * factor;
    return {
      foodSlug: ingrediente.foodSlug,
      amount:
        ingrediente.unit === 'unit' ? Math.max(1, Math.round(cantidad)) : roundTo(cantidad, 1),
      unit: ingrediente.unit,
    };
  });
}

export function recipeMacros(recipe: Recipe, scale = 1): MacroTargets {
  return itemsMacros(recipeToItems(recipe, scale));
}

/**
 * Rango de escalado util de una receta: la parte no escalable no se mueve, asi
 * que el factor efectivo sobre el total nunca llega a los extremos nominales.
 */
export function recipeScaleBounds(recipe: Recipe): { min: number; max: number } {
  const escalables = recipe.ingredients.filter((ingrediente) => ingrediente.isScalable);
  if (escalables.length === 0) return { min: 1, max: 1 };
  return {
    min: Math.max(...escalables.map((ingrediente) => ingrediente.scaleMinPct)),
    max: Math.min(...escalables.map((ingrediente) => ingrediente.scaleMaxPct)),
  };
}
